import numeral from 'numeral'
import { MILES_KM_CONVERSION_NUMBER } from 'app/settings/defaultValues'
import { docCookies } from 'utils/docCookies'

interface PurposeColor {
  class: string
  hex: string
}

interface PurposeColors {
  default: PurposeColor
  other: PurposeColor
  personal: PurposeColor
  unclassified: PurposeColor
}

interface StatusColor {
  color: string
  bgColor: string
}

interface PayPeriodStatusColors {
  [key: string]: StatusColor
}

interface ComplianceStatusColors {
  [key: string]: StatusColor
}

/**
 * TODO & Note from Mariano: according to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
 * The Math.round() static method returns the value of a number rounded to the nearest integer. So this seems pretty unstable, but I don't know
 * the reason why it's used, so temporarily I'll add a flag default to true.
 *
 * @param miles number
 * @param round boolean @default true
 * @param n number @default 1
 * @returns null | string
 */
export const milesToKms = (
  miles: number | string | null | undefined,
  round = true,
  n = 1
): string | null => {
  if (!miles) {
    return null
  }
  const zeroes = new Array(n < 1 ? 1 : n).fill(0)
  const milesValue = typeof miles === 'string' ? parseFloat(miles) : miles
  return numeral(
    round
      ? Math.round(milesValue * MILES_KM_CONVERSION_NUMBER)
      : milesValue * MILES_KM_CONVERSION_NUMBER
  ).format(`0.0${zeroes.join('')}`)
}

/**
 *
 * @param kms number
 * @returns null | string
 */
export const kmsToMiles = (
  kms: number | string | null | undefined
): string | null => {
  if (!kms) {
    return null
  }
  return numeral(
    (typeof kms === 'string' ? parseFloat(kms) : kms) /
      MILES_KM_CONVERSION_NUMBER
  ).format('0.0')
}

/**
 *
 * @param kms string | number
 * @returns number
 */
export const pureKmsToMiles = (kms: string | number): number => {
  return (
    (typeof kms === 'string' ? parseFloat(kms) : kms) /
    MILES_KM_CONVERSION_NUMBER
  )
}

export const kmsRateToMilesRate = (
  kmsRate: number | null | undefined
): string => {
  if (!kmsRate) {
    return '0'
  }
  return (kmsRate * MILES_KM_CONVERSION_NUMBER).toFixed(4)
}

export const getUniqueListBy = <T>(
  arr: T[] | null | undefined,
  key: string
): T[] => {
  if (!arr?.length || !key) return []
  return [
    ...new Map(
      arr
        .filter((item) => !!item[key as keyof T])
        .map((item) => [item[key as keyof T], item])
    ).values()
  ]
}

const otherPurposes: string[] = [
  'Medical',
  'Charity',
  'Other',
  'Commute',
  'Moving'
]
const purposeColors: PurposeColors = {
  default: {
    class: 'text-primary',
    hex: '#32B59F'
  },
  other: {
    class: 'text-warning',
    hex: '#E47E30'
  },
  personal: {
    class: 'text-secondary',
    hex: '#4A90E2'
  },
  unclassified: {
    class: 'text-dark',
    hex: '#333333'
  }
}

export const getPurposeColors = (purpose: string): PurposeColor => {
  if (otherPurposes.includes(purpose)) return purposeColors.other
  if (purpose === 'Personal') return purposeColors.personal
  if (['Unclassified', 'All purposes'].includes(purpose))
    return purposeColors.unclassified
  return purposeColors.default
}

export const downloadFile = (url: string, filename = 'download'): void => {
  const a = document.createElement('a')
  document.body.appendChild(a)
  a.style.cssText = 'display: none'
  a.href = url
  a.download = filename
  a.click()
  a.remove()
}

export const payPeriodStatusColors: PayPeriodStatusColors = {
  open: { color: '496BD3', bgColor: 'EDF6FF' },
  needs_approval: { color: 'F17200', bgColor: 'FFFBDA' },
  approving: { color: 'F17200', bgColor: 'FFFBDA' },
  scheduled: { color: '00826C', bgColor: 'EAFFFA' },
  marked_as_paid: { color: '00826C', bgColor: 'E2FFED' },
  needs_payment: { color: 'F17200', bgColor: 'FFF1D9' },
  payment_sent: { color: '00826C', bgColor: 'D6FFE6' },
  paying: { color: '00826C', bgColor: 'D6FFE6' },
  partially_paid: { color: 'F17200', bgColor: 'FFF1D9' },
  paid: { color: '00826c', bgColor: 'eafffa' },
  approved: { color: '00826C', bgColor: 'D6FFE6' },
  default: { bgColor: 'eafffa', color: '00826c' }
}

export const complianceStatusColors: ComplianceStatusColors = {
  default: { bgColor: 'EBEBEB', color: '73767D' },
  unsubmitted: { bgColor: 'EBEBEB', color: '73767D' },
  pending_review: { bgColor: 'fff1d9', color: 'f17200' },
  in_review: { bgColor: 'fffbda', color: 'f17200' },
  approved: { bgColor: 'eafffa', color: '00826c' },
  rejected: { bgColor: 'fff1f1', color: 'eb001c' },
  compliant: { bgColor: 'eafffa', color: '00826c' },
  'non-compliant': { bgColor: 'fff1f1', color: 'eb001c' }
}

export const formatAsUSD = (value: number): string => {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

/**
 * This one work for images that were uploaded with ActiveRecord Ruby on Rails built-in functionality
 * @param path string
 * @returns string (Image Url)
 */
export const getImageUrl = (path: string): string => {
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL ?? '').replace(
    /\/v\d\//i,
    ''
  )
  return `${apiBaseUrl}${path}`
}
/**
 * Set auth token cookie
 * @param token string
 * @returns void
 */

export const setAuthTokenCookie = (token: string): void => {
  docCookies.setItem('auth_token', token, {
    bSecure: true
  })
}
